import {
  DataObjectType,
  DataObjectVersionSourceType,
  formatCodeName,
  formatDate,
  formatDateTimeShort,
  getGroupDescriptionRef,
  SystemClassifierItemDto,
  Table,
  useDataObjectVersionSourcesQuery,
  useSystemClassifierItemsBy,
} from '@atlas-frontend/sdk';
import React, { FC, memo, useMemo } from 'react';
import translations from './SourcesTable.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { path } from '../../../paths';
import { UiLink } from '../../../paths/UiLink';

const { addTranslations, t } = i18nNsFactory('visual-validation-sources-table');
addTranslations(translations);

const columnHelper = createColumnHelper<DataObjectVersionSourceType>();

export const SourcesTable: React.FC<{ versionId: number }> = ({
  versionId,
}) => {
  const { data: sources = [], isFetching } =
    useDataObjectVersionSourcesQuery(versionId);
  const { DATA_OBJECT_VER_STATUS: versionStatuses } =
    useSystemClassifierItemsBy(['DATA_OBJECT_VER_STATUS']);

  const columns = useMemo(
    () => [
      columnHelper.accessor((rowData) => formatCodeName(rowData), {
        id: 'name',
        header: t('column.name'),
      }),
      columnHelper.accessor((rowData) => formatDate(rowData.valueDate), {
        id: 'valueDate',
        header: t('column.valueDate'),
        filterFn: 'formattedDate',
      }),
      columnHelper.display({
        id: 'version',
        header: t('column.version'),
        cell: (props) => (
          <VersionCell
            {...props}
            versionStatuses={versionStatuses}
            outputDataVersionId={versionId}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [versionId, versionStatuses],
  );

  return (
    <Table
      name="visual-validation-sources-table"
      columns={columns}
      data={sources}
      isLoading={isFetching}
    />
  );
};

const VersionCell: FC<
  CellContext<DataObjectVersionSourceType, any> & {
    versionStatuses: Map<string | number | boolean, SystemClassifierItemDto>;
    outputDataVersionId?: number;
  }
> = memo((props) => {
  const {
    descriptionId,
    groupId,
    type,
    valueDate,
    versionId,
    versionNumber,
    minorNumber,
    calculationTime,
    versionStatusId,
  } = props.row.original;
  const statusName = props.versionStatuses.get(versionStatusId)?.name;
  const id = `${descriptionId}-${versionId}-version-link`;

  return type === DataObjectType.IN ? (
    <UiLink
      id={id}
      path={path.inputDataView}
      target="_blank"
      params={{
        id: descriptionId,
        date: valueDate,
        type: 'description',
        inputDataVersionId: versionId,
        outputDataVersionId: props.outputDataVersionId,
      }}
    >
      {`${formatDateTimeShort(calculationTime)} ${statusName}`}
    </UiLink>
  ) : (
    <UiLink
      id={id}
      path={path.visualValidationCrossTable}
      target="_blank"
      params={{
        groupDescriptionRef: getGroupDescriptionRef(groupId, descriptionId),
        valueDate,
        version: versionId,
      }}
    >
      {`${minorNumber ? `${versionNumber}.${minorNumber}` : versionNumber || ''} ${statusName}`}
    </UiLink>
  );
});
